import {
  Badge,
  Box,
  Divider,
  Flex,
  HStack,
  IconButton,
  Image,
  Text,
  Tooltip,
  VStack,
  keyframes,
} from '@chakra-ui/react';
import { MouseEvent, useState } from 'react';
import { MatchResponse, MatchState } from 'api/responseTypes';
import { ReactComponent as mapPinSVG } from 'assets/map-pin.svg';
import { ReactComponent as PlaceholderTeamLogo } from 'assets/placeholder.svg';
import { useEventTracking } from 'hooks/useEventTracking';
import { FormattedMessage } from 'react-intl';
import { Share2 } from 'lucide-react';
import { shareEvent } from 'utils/shareEvent';

const pulsate = keyframes`
  0% { 
    opacity: 1; 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 0.7);
  }
  50% { 
    opacity: 0.9; 
    transform: scale(1.02);
    box-shadow: 0 0 0 8px rgba(255, 235, 59, 0);
  }
  100% { 
    opacity: 1; 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 0);
  }
`;

const pulsateAnimation = `${pulsate} 2s ease-in-out infinite`;

interface GameCardProps extends MatchResponse {
  onCardClick: () => void;
}

interface TeamLogoProps {
  team: { name: string; logo: string };
  state: MatchState;
}

const TeamLogo = ({ team, state }: TeamLogoProps) => {
  return (
    <VStack
      spacing={2}
      flex={1}
      maxW={{ base: '100px', sm: '110px', md: '130px' }}
      minW='0'
      alignItems='center'
    >
      <Box
        position='relative'
        width={{ base: '52px', md: '64px' }}
        height={{ base: '52px', md: '64px' }}
        borderRadius='full'
        bg='dark.D0'
        display='flex'
        alignItems='center'
        justifyContent='center'
        borderWidth='2px'
        borderColor={state === MatchState.SOLD_OUT ? 'dark.D200' : 'dark.D100'}
        transition='all 0.2s ease'
        overflow='hidden'
        flexShrink={0}
      >
        <Image
          src={team.logo}
          alt={`${team.name} logo`}
          boxSize={{ base: '40px', md: '48px' }}
          filter={state === MatchState.SOLD_OUT ? 'grayscale(100%) opacity(0.6)' : 'none'}
          borderRadius='50%'
          objectFit='contain'
          objectPosition='center'
          fallback={<PlaceholderTeamLogo />}
        />
      </Box>
      <Tooltip label={team.name} hasArrow>
        <Text
          fontSize='xs'
          fontWeight='500'
          color={state === MatchState.SOLD_OUT ? 'dark.D300' : 'dark.D700'}
          textStyle='caption'
          textAlign='center'
          noOfLines={2}
          lineHeight='1.4'
          width='100%'
          minH='18px'
          maxH='36px'
        >
          {team.name}
        </Text>
      </Tooltip>
    </VStack>
  );
};

export const GameCard = ({
  state,
  onCardClick,
  date,
  time,
  stadium,
  startingPrice,
  currency,
  teamA,
  teamB,
  title,
  subtitle,
  eventKey,
  isFreeEvent,
  hasTimeslots,
}: GameCardProps) => {
  const [isSharing, setIsSharing] = useState(false);
  const { eventTracking } = useEventTracking();

  const handleShareClick = async (e: MouseEvent) => {
    e.stopPropagation();
    if (isSharing) return;

    const eventName =
      teamA?.name && teamB?.name ? `${teamA.name} vs ${teamB.name}` : title || eventKey;

    eventTracking.share.buttonClicked({
      typeOfEvent: 'match',
      eventName,
    });

    setIsSharing(true);
    try {
      await shareEvent(eventName, eventKey, false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '';

      if (errorMessage.includes('AbortError') || errorMessage.includes('cancel')) {
        return;
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Box
      as='li'
      listStyleType='none'
      bg='white'
      borderRadius='12px'
      borderWidth='1px'
      borderColor={state === MatchState.SOLD_OUT ? 'dark.D100' : 'dark.D100'}
      overflow='hidden'
      cursor={
        state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'not-allowed' : 'pointer'
      }
      pointerEvents={
        state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'none' : 'unset'
      }
      onClick={onCardClick}
      transition='all 0.2s ease'
      _hover={{
        borderColor:
          state === MatchState.SOLD_OUT || state === MatchState.PREVIEW
            ? 'dark.D100'
            : 'green.G500',
        shadow: state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'none' : 'md',
        transform:
          state === MatchState.SOLD_OUT || state === MatchState.PREVIEW
            ? 'none'
            : 'translateY(-2px)',
      }}
      boxShadow={state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'none' : 'sm'}
      width='100%'
      height='100%'
      display='flex'
      flexDirection='column'
      position='relative'
      tabIndex={0}
      aria-label={`${teamA.name} vs ${teamB.name} match${date ? ` on ${date}` : ''}${
        stadium ? ` at ${stadium}` : ''
      }`}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (
          (e.key === 'Enter' || e.key === ' ') &&
          state !== MatchState.SOLD_OUT &&
          state !== MatchState.PREVIEW
        ) {
          e.preventDefault();
          onCardClick();
        }
      }}
    >
      {state !== MatchState.PREVIEW && (
        <IconButton
          aria-label='Share event'
          icon={<Share2 size={16} />}
          size='sm'
          position='absolute'
          top={3}
          left={3}
          zIndex={2}
          bg='#E0F2F1'
          color='#4DB6AC'
          borderRadius='lg'
          _hover={{ bg: '#B2DFDB', color: '#00897B' }}
          _active={{ bg: '#80CBC4' }}
          isDisabled={isSharing}
          onClick={handleShareClick}
        />
      )}
      <VStack align='stretch' spacing={0} p={{ base: 5, md: 5 }} flex={1}>
        {/* Title Section */}
        {(title || subtitle) && (
          <Flex
            justify='center'
            align='center'
            gap={1}
            mb={3}
            flexWrap='nowrap'
            minH='20px'
            overflow='hidden'
          >
            {title && (
              <Text
                id='gameCardTitle'
                fontSize='sm'
                fontWeight='600'
                color={state === MatchState.SOLD_OUT ? 'dark.D400' : 'dark.D700'}
                textStyle='bodySmallMedium'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
                overflow='hidden'
              >
                {title}
              </Text>
            )}
            {title && subtitle && (
              <Text fontSize='sm' color='dark.D300' fontWeight='400' flexShrink={0}>
                •
              </Text>
            )}
            {subtitle && (
              <Text
                fontSize='sm'
                fontWeight='600'
                color={state === MatchState.SOLD_OUT ? 'dark.D400' : 'dark.D700'}
                textStyle='bodySmallMedium'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
                overflow='hidden'
              >
                {subtitle}
              </Text>
            )}
          </Flex>
        )}

        {/* Teams Section */}
        <Flex
          justifyContent='space-around'
          alignItems='center'
          direction='row'
          py={{ base: 3, md: 4 }}
          gap={{ base: 2, md: 4 }}
          minH={{ base: '110px', md: '130px' }}
        >
          {/* Team A */}
          <TeamLogo team={teamA} state={state} />

          {/* Date/Time Section */}
          <VStack
            spacing={1}
            alignItems='center'
            minW={{ base: '70px', sm: '80px', md: '88px' }}
            flexShrink={0}
            minH={{ base: '44px', md: '48px' }}
          >
            {hasTimeslots ? (
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight='600'
                color={state === MatchState.SOLD_OUT ? 'dark.D300' : 'dark.D900'}
                textStyle='bodySmallSemibold'
                textAlign='center'
              >
                حدث متعدد الاوقات
              </Text>
            ) : (
              <>
                {date && (
                  <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    fontWeight='600'
                    color={state === MatchState.SOLD_OUT ? 'dark.D300' : 'dark.D900'}
                    textStyle='bodySmallSemibold'
                  >
                    {date}
                  </Text>
                )}
                <Text
                  fontSize='xs'
                  fontWeight='500'
                  color={state === MatchState.SOLD_OUT ? 'dark.D200' : 'dark.D500'}
                  textStyle='caption'
                >
                  {time}
                </Text>
              </>
            )}
          </VStack>

          {/* Team B */}
          <TeamLogo team={teamB} state={state} />
        </Flex>

        <Divider borderColor='dark.D100' my={2} />

        {/* Footer Section */}
        <Flex
          justifyContent='space-between'
          alignItems='center'
          pt={2}
          flexWrap='nowrap'
          minH='44px'
          w='100%'
        >
          {/* Price Badge */}
          <HStack
            spacing={2}
            alignItems='center'
            flexWrap='nowrap'
            minW={{ base: '150px', md: '180px' }}
            flexShrink={0}
          >
            <Badge
              bg={
                state === MatchState.SOLD_OUT
                  ? 'red'
                  : state === MatchState.PREVIEW
                  ? 'yellow.Y500'
                  : isFreeEvent
                  ? 'teal.500'
                  : 'green.G500'
              }
              color={state === MatchState.PREVIEW ? 'dark.D900' : 'white'}
              px={3}
              py={1.5}
              borderRadius='full'
              fontSize='sm'
              fontWeight='600'
              boxShadow={
                state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'none' : 'sm'
              }
              textStyle='bodySmallSemibold'
              minW={{ base: '92px', md: '110px' }}
              textAlign='center'
              whiteSpace='nowrap'
              animation={state === MatchState.PREVIEW ? pulsateAnimation : 'none'}
            >
              {state === MatchState.SOLD_OUT ? (
                <FormattedMessage id='gameCard.soldOut' defaultMessage='Sold out' />
              ) : state === MatchState.PREVIEW ? (
                <FormattedMessage id='gameCard.preview' defaultMessage='Preview' />
              ) : isFreeEvent ? (
                <FormattedMessage id='freeEvent.label' defaultMessage='Free' />
              ) : (
                `${new Intl.NumberFormat().format(startingPrice)} ${currency}`
              )}
            </Badge>
            {!isFreeEvent && (
              <Text
                fontSize='xs'
                color='dark.D500'
                fontWeight='500'
                textStyle='bodySmallRegular'
                whiteSpace='nowrap'
                visibility={
                  state === MatchState.SOLD_OUT || state === MatchState.PREVIEW
                    ? 'hidden'
                    : 'visible'
                }
              >
                <FormattedMessage id='gameCard.from' defaultMessage='From' />
              </Text>
            )}
          </HStack>

          <HStack spacing={2} alignItems='center' minW={0} flexShrink={1} justifyContent='flex-end'>
            {stadium && (
              <HStack spacing={1.5} alignItems='center' minW={0} flexShrink={1} dir='rtl'>
                <Box
                  as={mapPinSVG}
                  boxSize='16px'
                  color={state === MatchState.SOLD_OUT ? 'dark.D300' : 'green.G500'}
                  filter={state === MatchState.SOLD_OUT ? 'grayscale(1)' : 'none'}
                  flexShrink={0}
                />
                <Text
                  fontSize='xs'
                  fontWeight='500'
                  color={state === MatchState.SOLD_OUT ? 'dark.D400' : 'green.G500'}
                  textStyle='bodySmallRegular'
                  noOfLines={1}
                  minW={0}
                  overflow='hidden'
                  textOverflow='ellipsis'
                >
                  {stadium}
                </Text>
              </HStack>
            )}
          </HStack>
        </Flex>
      </VStack>
    </Box>
  );
};
